import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  UtilServiceFormRule,
  UtilServiceFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultUtilService } from "@factory/form/form-default-util-service.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseUtilServiceModel } from "@factory/model/query-model-util-service";
import {
  useQyDeleteUtilService,
  useQyGetUtilServiceById,
  useQyUpdateUtilService,
} from "@factory/query/util-service.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditUtilService() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultUtilService();
  const navigate = useNavigate();
  const { mapUpdateUtilService, mapDeleteUtilService } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { utilServiceId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID UtilService
  const handleGetApiSuccess = (data: GetResponseUtilServiceModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("tenant", responseData?.tenant || "");
    setValue("category", responseData?.category || "");
    setValue("name", responseData?.name || "");
    setValue("description", responseData?.description || "");
    setValue("amount", responseData?.amount || 0);
  };
  const {
    data: utilServiceResponse,
    isLoading,
    isError: utilServiceError,
  } = useQyGetUtilServiceById(utilServiceId || "", handleGetApiSuccess);
  const utilServiceData = utilServiceResponse?.data?.[0];

  // API UPDATE UtilService
  const handleUpdateApiSuccess = () => {
    showSuccess("UtilService updated successfully");
    navigate(`../`);
  };
  const { mutate: updateUtilService, isLoading: isEditLoading } =
    useQyUpdateUtilService(handleUpdateApiSuccess);

  // API DELETE UtilService
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("UtilService deleted successfully");
  };
  const { mutate: deleteUtilService } = useQyDeleteUtilService(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<UtilServiceFormSchema>({
    defaultValues: getDefault(utilServiceData),
    resolver: zodResolver(UtilServiceFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: UtilServiceFormSchema) => {
    if (!utilServiceData || !utilServiceData.code) {
      throw new Error("No utilService data");
    }

    const formData = mapUpdateUtilService(form, utilServiceData.code);
    updateUtilService(formData);
  };
  const handleValidateError = (err: FieldErrors<UtilServiceFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!utilServiceData || !utilServiceData.code) {
      throw new Error("No utilService data found");
    }

    const form = mapDeleteUtilService(utilServiceData.code);
    deleteUtilService(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    utilServiceId,
    utilServiceData,
    isLoading,
    utilServiceError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
